
/* ============================== src/hooks/useInputModality.js ============================== */
// src/hooks/useInputModality.js
import { useEffect } from "react";

/**
 * Трекер модальности ввода.
 *
 * Устанавливает data-input="keyboard" | "pointer" на <html>. CSS через
 * этот атрибут решает, показывать ли :focus-visible.
 *
 * Зачем. Браузеры включают :focus-visible на любом keydown, включая
 * Esc и функциональные клавиши. Сценарий бага: клик мышью по карточке
 * → фокус на карточке, :focus-visible не показан → открылась модалка
 * → Esc → браузер считает «последний ввод был клавиатурный» и рисует
 * кольцо на карточке. Пользователь взаимодействовал мышью, кольцо
 * лишнее.
 *
 * NON_KEYBOARD_KEYS - «не навигационные» клавиши: модификаторы, Esc,
 * F1–F12, служебные (CapsLock и т. п.). Они не должны переключать
 * режим на keyboard - иначе Escape, закрывающий модалку, сам же и
 * включает кольцо. Всё остальное (Tab, Enter, Space, стрелки, буквы,
 * цифры) - навигация или ввод, честный keyboard-режим.
 *
 * pointerdown вместо mousedown + touchstart: одно событие покрывает
 * мышь, тач и стилус.
 *
 * Capture-фаза: срабатывает раньше React-обработчиков и любых других
 * слушателей; атрибут обновится до того, как начнётся обработка
 * конкретного взаимодействия.
 */
const NON_KEYBOARD_KEYS = new Set([
  "Escape",
  "Esc",
  "Shift",
  "Control",
  "Alt",
  "Meta",
  "CapsLock",
  "NumLock",
  "ScrollLock",
  "F1",
  "F2",
  "F3",
  "F4",
  "F5",
  "F6",
  "F7",
  "F8",
  "F9",
  "F10",
  "F11",
  "F12",
]);

export function useInputModality() {
  useEffect(() => {
    const root = document.documentElement;

    const onKeyDown = (e) => {
      if (NON_KEYBOARD_KEYS.has(e.key)) return;
      root.dataset.input = "keyboard";
    };
    const onPointerDown = () => {
      root.dataset.input = "pointer";
    };

    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("pointerdown", onPointerDown, true);

    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
      delete root.dataset.input;
    };
  }, []);
}
